import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useUppercaseInput } from '@/hooks/useUppercaseInput';
import { clientService } from '@/services/clients';
import type { Client } from '@/types/api';
import { resolveApiErrorMessage } from '@/utils/errors';

const phoneSchema = z
  .string()
  .trim()
  .transform((value) => {
    const digits = value.replace(/\D/g, '');
    // Se vazio após remover não-dígitos, retorna undefined
    if (digits.length === 0) {
      return undefined;
    }
    // Valida comprimento
    if (digits.length !== 10 && digits.length !== 11) {
      throw new z.ZodError([
        {
          code: 'custom',
          path: ['phone'],
          message: 'Celular inválido. Informe um número com 10 ou 11 dígitos',
        },
      ]);
    }
    return digits;
  });

const clientSchema = z.object({
  name: z.string().min(2, 'Nome é obrigatório e deve ter pelo menos 2 caracteres'),
  phone: phoneSchema.refine((val) => val !== undefined, {
    message: 'Celular é obrigatório',
  }),
  email: z
    .string()
    .email('E-mail inválido')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  address: z.string().optional(),
  birthDate: z
    .string()
    .optional()
    .refine((value) => !value || !Number.isNaN(Date.parse(value)), 'Data de nascimento inválida'),
  notes: z.string().max(1000, 'Máximo de 1000 caracteres').optional(),
});

type ClientFormValues = z.infer<typeof clientSchema>;

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (client: Client) => void;
  client?: Client;
}

export const ClientModal = ({ isOpen, onClose, onSaved, client }: ClientModalProps) => {
  const { handleUppercaseChange } = useUppercaseInput();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      birthDate: '',
      notes: '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: ClientFormValues) => {
      const payload = {
        name: values.name.trim().toUpperCase(),
        phone: values.phone || '', // Celular é obrigatório - garantir que seja string
        email: values.email?.trim() || undefined,
        address: values.address?.trim()?.toUpperCase() || null,
        birthDate: values.birthDate && values.birthDate.length > 0 ? values.birthDate : null,
        notes: values.notes?.trim()?.toUpperCase() || null,
      };
      return client ? clientService.update(client.id, payload) : clientService.create(payload);
    },
    onSuccess: (savedClient) => {
      toast.success(client ? 'Cliente atualizado' : 'Cliente criado');
      reset();
      onSaved?.(savedClient);
      onClose();
    },
    onError: (error) => toast.error(resolveApiErrorMessage(error, 'Erro ao salvar cliente')),
  });

  React.useEffect(() => {
    if (client && isOpen) {
      reset({
        name: client.name,
        email: client.email ?? '',
        phone: client.phone ? client.phone.replace(/\D/g, '') : '',
        address: client.address ?? '',
        birthDate: client.birthDate ? client.birthDate.slice(0, 10) : '',
        notes: client.notes ?? '',
      });
    } else if (!client && isOpen) {
      reset({ name: '', email: '', phone: '', address: '', birthDate: '', notes: '' });
    }
  }, [client, isOpen, reset]);

  const onSubmit = (values: ClientFormValues) => {
    mutation.mutate(values);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const sanitizePhone = (value: string) => value.replace(/\D/g, '').slice(0, 11);

  const formatPhone = (value: string) => {
    const digits = sanitizePhone(value ?? '');
    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 7);
    const part3 = digits.slice(7, 11);

    let formatted = '';

    if (part1) {
      formatted += `(${part1}`;
      if (part1.length === 2) {
        formatted += ')';
      }
    }

    if (part2) {
      formatted += ` ${part2}`;
    }

    if (part3) {
      formatted += `-${part3}`;
    }

    return formatted.trim();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Novo cliente"
      description="Cadastre um cliente rapidamente"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="name">
            Nome
          </label>
          <Input
            id="name"
            placeholder="Nome completo"
            error={errors.name?.message}
            {...register('name', {
              onChange: handleUppercaseChange,
            })}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="email">
            E-mail
          </label>
          <Input
            id="email"
            type="email"
            placeholder="email@cliente.com"
            error={errors.email?.message}
            {...register('email')}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="phone">
            Telefone
          </label>
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <Input
                id="phone"
                placeholder="(11) 98888-7777"
                value={formatPhone(field.value ?? '')}
                onChange={(event) => field.onChange(sanitizePhone(event.target.value))}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                inputMode="tel"
                maxLength={15}
              />
            )}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="address">
            Endereço
          </label>
          <Input
            id="address"
            placeholder="Rua, número"
            {...register('address', {
              onChange: handleUppercaseChange,
            })}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="birthDate">
            Data de nascimento
          </label>
          <Input
            id="birthDate"
            type="date"
            error={errors.birthDate?.message}
            {...register('birthDate')}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="notes">
            Observações
          </label>
          <textarea
            id="notes"
            className="h-24 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            placeholder="Informações importantes sobre o cliente"
            {...register('notes', {
              onChange: handleUppercaseChange,
            })}
          />
          {errors.notes && <p className="mt-1 text-xs text-danger">{errors.notes.message}</p>}
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvando...' : client ? 'Atualizar' : 'Criar cliente'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
